import { Component, OnInit } from '@angular/core';
import { DataExchangeService, DataSet, ExchangeField, ImportLog, ImportRow, RowResult } from './data-exchange.service';

type Stage = 'choose' | 'preview' | 'done';
type Filter = 'all' | 'new' | 'overwrite' | 'unchanged' | 'error';

/**
 * Settings > Data Import (administrators only; replaces twin-ci Database Import). The CSV is read in
 * the browser and never uploaded as a file: rows go to the API `batch` (1000) at a time. Each batch
 * is previewed (New / Overwrite with the changing fields / Unchanged / Error), then imported; rows
 * with errors are skipped and can be downloaded. The next batch is shown only after this one.
 */
@Component({
    selector: 'app-data-import',
    templateUrl: './data-import.component.html',
    styleUrls: ['./data-exchange.component.scss']
})
export class DataImportComponent implements OnInit {
    dataSets: DataSet[] = [];
    set: DataSet = null;
    batch = 1000;
    loaded = false;
    error = '';

    stage: Stage = 'choose';
    fileName = '';
    rows: ImportRow[] = [];            // every data row of the file
    rawRows: string[][] = [];          // the same rows as read, for the error CSV
    header: string[] = [];
    matched: ExchangeField[] = [];     // columns that will be imported
    ignored: string[] = [];            // columns that are read-only or unknown
    batchIndex = 0;
    preview: RowResult[] = [];
    filter: Filter = 'all';
    busy = false;
    logId = 0;
    totals = { created: 0, updated: 0, unchanged: 0, skipped: 0 };
    lastCounts = null;
    // Overwrites are permanent: the batch only imports once this is ticked (reset every batch).
    confirmOverwrite = false;
    errorRows: { result: RowResult, raw: string[] }[] = [];
    logs: ImportLog[] = [];

    constructor(private api: DataExchangeService) { }

    ngOnInit() {
        this.api.catalog().subscribe(
            res => {
                this.dataSets = (res.data_sets || []).filter(s => s.import);
                this.batch = res.batch || 1000;
                this.set = this.dataSets[0] || null;
                this.loaded = true;
            },
            err => this.error = typeof err === 'string' ? err : 'Data sets could not be loaded.'
        );
        this.loadLogs();
    }

    private loadLogs() {
        this.api.logs().subscribe(res => this.logs = res.logs || [], () => { });
    }

    get importableLabels(): string {
        return this.set ? this.set.fields.filter(f => f.import).map(f => f.label).join(', ') : '';
    }

    /** "Export a backup first": the export section above, on the same page. */
    toExport() {
        const target = document.getElementById('dataExportTitle');
        if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    // ---- File ----

    onFile(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files && input.files[0];
        input.value = '';
        this.error = '';
        if (!file) {
            return;
        }
        if (!/\.csv$/i.test(file.name)) {
            this.error = 'Choose a .csv file.';
            return;
        }
        const reader = new FileReader();
        reader.onload = () => this.readCsv(file.name, String(reader.result || ''));
        reader.onerror = () => this.error = 'The file could not be read.';
        reader.readAsText(file);
    }

    private readCsv(name: string, text: string) {
        const table = this.api.parseCsv(text);
        if (table.length < 2) {
            this.error = 'The file has no data rows under its header row.';
            return;
        }
        const header = table[0].map(h => h.trim());
        const byLabel: { [label: string]: ExchangeField } = {};
        this.set.fields.forEach(f => byLabel[f.label.toLowerCase()] = f);
        const columns: { index: number, field: ExchangeField }[] = [];
        const ignored: string[] = [];
        header.forEach((h, index) => {
            const field = byLabel[h.toLowerCase()];
            if (field && field.import) {
                columns.push({ index, field });
            } else if (h) {
                ignored.push(h);
            }
        });
        const idField = this.set.fields.find(f => f.type === 'id');
        if (!columns.some(c => c.field.type !== 'id')) {
            this.error = 'None of the columns can be imported into ' + this.set.label + '. Use the column names of an export: '
                + this.importableLabels + '.';
            return;
        }
        this.fileName = name;
        this.header = header;
        this.matched = columns.map(c => c.field);
        this.ignored = ignored;
        this.rawRows = table.slice(1);
        this.rows = this.rawRows.map((cells, i) => {
            const values: { [key: string]: string } = {};
            columns.forEach(c => values[c.field.key] = cells[c.index] !== undefined ? cells[c.index] : '');
            if (idField && values[idField.key] === undefined) {
                values[idField.key] = '';
            }
            return { line: i + 1, values };
        });
        this.batchIndex = 0;
        this.logId = 0;
        this.totals = { created: 0, updated: 0, unchanged: 0, skipped: 0 };
        this.errorRows = [];
        this.lastCounts = null;
        this.validateBatch();
    }

    // ---- Batches ----

    get batchCount(): number {
        return Math.ceil(this.rows.length / this.batch);
    }

    get batchStart(): number {
        return this.batchIndex * this.batch + 1;
    }

    get batchEnd(): number {
        return Math.min((this.batchIndex + 1) * this.batch, this.rows.length);
    }

    private batchRows(): ImportRow[] {
        return this.rows.slice(this.batchIndex * this.batch, (this.batchIndex + 1) * this.batch);
    }

    private validateBatch() {
        this.busy = true;
        this.preview = [];
        this.filter = 'all';
        this.confirmOverwrite = false;
        this.api.validate(this.set.key, this.batchRows()).subscribe(
            res => {
                this.busy = false;
                this.preview = res.rows || [];
                this.stage = 'preview';
            },
            err => {
                this.busy = false;
                this.error = typeof err === 'string' ? err : 'These rows could not be checked.';
            }
        );
    }

    count(status: Filter): number {
        return status === 'all' ? this.preview.length : this.preview.filter(r => r.status === status).length;
    }

    get shown(): RowResult[] {
        return this.filter === 'all' ? this.preview : this.preview.filter(r => r.status === this.filter);
    }

    get toWrite(): number {
        return this.count('new') + this.count('overwrite');
    }

    get canImport(): boolean {
        return !this.busy && (this.count('overwrite') === 0 || this.confirmOverwrite);
    }

    importBatch() {
        this.busy = true;
        this.error = '';
        const start = this.batchIndex * this.batch;
        this.api.import(this.set.key, this.batchRows(), this.fileName, this.rows.length, this.logId).subscribe(
            res => {
                this.busy = false;
                this.logId = res.log_id;
                this.lastCounts = res.counts;
                Object.keys(this.totals).forEach(k => this.totals[k] += res.counts[k] || 0);
                (res.rows || []).filter(r => r.status === 'error').forEach(r => {
                    this.errorRows.push({ result: r, raw: this.rawRows[r.line - 1] || [] });
                });
                this.loadLogs();
                if (start + this.batch < this.rows.length) {
                    this.batchIndex++;
                    this.validateBatch();
                } else {
                    this.stage = 'done';
                }
            },
            err => {
                this.busy = false;
                this.error = typeof err === 'string' ? err : 'This batch could not be imported. Nothing in it was saved.';
            }
        );
    }

    cancel() {
        this.stage = this.logId ? 'done' : 'choose';
        if (!this.logId) {
            this.rows = [];
        }
    }

    restart() {
        this.stage = 'choose';
        this.rows = [];
        this.preview = [];
        this.error = '';
    }

    /** The skipped rows as they were in the file, with an Errors column first. */
    downloadErrors() {
        const lines = [['Row', 'Errors'].concat(this.header)];
        this.errorRows.forEach(e => lines.push([String(e.result.line), e.result.errors.join(' ')].concat(e.raw)));
        const blob = new Blob(['﻿' + this.api.toCsv(lines)], { type: 'text/csv;charset=utf-8' });
        this.api.save(blob, this.fileName.replace(/\.csv$/i, '') + '-errors.csv');
    }

    statusLabel(status: string): string {
        return ({ new: 'New', overwrite: 'Overwrite', unchanged: 'Unchanged', error: 'Error' } as any)[status] || status;
    }

    trackLine(index: number, row: RowResult) {
        return row.line;
    }
}
